import { useEffect, useState } from 'react';
import {
  ArrowClockwiseIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  CopyIcon,
  KeyIcon,
  PulseIcon,
  RobotIcon,
  ShieldWarningIcon,
  SlidersHorizontalIcon,
  TrashIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { PageContainer } from '@/components/layout/PageContainer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  DetailBlock,
  DetailHeader,
  DetailSection,
  DetailShell,
  StatusDot,
} from '@/components/shared/settings/EditorialDetail';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { env } from '@/utils/env';
import {
  mcpService,
  type McpConnection,
  type McpApiKey,
  type CreateApiKeyDto,
} from '@/services/integrations/mcp.service';
import { formatDistanceToNow } from 'date-fns';
import { createLogger } from '@/lib/logger';
import { formatDateUserTz } from '@/utils/date';

const logger = createLogger('pages/settings/mcp');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MCP_STREAM_URL = `${env.apiUrl}/integrations/mcp/stream`;

type ClientTab = 'claude' | 'chatgpt' | 'cursor' | 'windsurf' | 'vscode' | 'jetbrains' | 'openclaw' | 'stdio';

function buildConfigSnippet(tab: ClientTab, apiKey: string): string {
  const key = apiKey || 'YOUR_API_KEY_HERE';
  switch (tab) {
    case 'claude':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              url: MCP_STREAM_URL,
            },
          },
        },
        null,
        2,
      );
    case 'chatgpt':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              url: MCP_STREAM_URL,
            },
          },
        },
        null,
        2,
      );
    case 'cursor':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              url: MCP_STREAM_URL,
              headers: { 'x-api-key': key },
            },
          },
        },
        null,
        2,
      );
    case 'windsurf':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              serverUrl: MCP_STREAM_URL,
              headers: { 'x-api-key': key },
            },
          },
        },
        null,
        2,
      );
    case 'vscode':
      return JSON.stringify(
        {
          servers: {
            scrambl: {
              type: 'http',
              url: MCP_STREAM_URL,
              headers: { 'x-api-key': key },
            },
          },
        },
        null,
        2,
      );
    case 'jetbrains':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              url: MCP_STREAM_URL,
              headers: { 'x-api-key': key },
            },
          },
        },
        null,
        2,
      );
    case 'openclaw':
      return JSON.stringify(
        {
          url: MCP_STREAM_URL,
          transport: 'streamable-http',
          headers: { 'x-api-key': key },
        },
        null,
        2,
      );
    case 'stdio':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              command: 'npx',
              args: ['@scrambl/mcp-server'],
              env: {
                SCRAMBL_API_URL: env.apiUrl,
                SCRAMBL_API_KEY: key,
              },
            },
          },
        },
        null,
        2,
      );
  }
}

const CLIENT_TABS: { value: ClientTab; label: string }[] = [
  { value: 'chatgpt', label: 'ChatGPT' },
  { value: 'claude', label: 'Claude Desktop' },
  { value: 'cursor', label: 'Cursor' },
  { value: 'windsurf', label: 'Windsurf' },
  { value: 'vscode', label: 'VS Code Copilot' },
  { value: 'jetbrains', label: 'JetBrains' },
  { value: 'openclaw', label: 'OpenClaw' },
  { value: 'stdio', label: 'Local (stdio)' },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="ghost" size="sm" onClick={handleCopy} className="gap-1.5">
      {copied ? (
        <CheckCircleIcon className="h-4 w-4 text-green-500" />
      ) : (
        <CopyIcon className="h-4 w-4" />
      )}
      {label && <span className="text-xs">{label}</span>}
    </Button>
  );
}

function CodeBlock({ code, className }: { code: string; className?: string }) {
  return (
    <div className={cn('relative group', className)}>
      <pre className="p-4 rounded-lg bg-zinc-900 text-zinc-100 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{code}</code>
      </pre>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <CopyButton text={code} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connection Status Section
// ---------------------------------------------------------------------------

function ConnectionStatus({
  connection,
  onEnable,
  enabling,
}: {
  connection: McpConnection | null;
  onEnable: () => void;
  enabling: boolean;
}) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await mcpService.getCapabilities();
      setTestResult('success');
      toast.success('Connection successful', {
        description: 'MCP server is responding correctly.',
      });
    } catch {
      setTestResult('error');
      toast.error('Connection failed', {
        description: 'Could not reach the MCP server. Check your configuration.',
      });
    } finally {
      setTesting(false);
    }
  };

  if (!connection) {
    return (
      <div className="flex flex-col gap-3 rounded-md border border-dashed border-border/60 bg-muted/15 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-primary/10 text-primary">
            <RobotIcon className="h-4 w-4" weight="fill" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">MCP integration is off</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              The Model Context Protocol lets AI assistants like Claude, Cursor, and
              others read and manage your Scrambl data via a secure API.
            </p>
          </div>
        </div>
        <Button size="sm" onClick={onEnable} disabled={enabling} className="self-start">
          {enabling ? (
            <>
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
              Enabling…
            </>
          ) : (
            'Enable MCP integration'
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">Server URL</span>
        <StatusDot
          tone={testResult === 'error' ? 'error' : 'ok'}
          label={
            testResult === 'error'
              ? 'Error'
              : testResult === 'success'
                ? 'Connected'
                : 'Active'
          }
        />
      </div>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md bg-muted/40 px-2.5 py-1.5 font-mono text-xs">
          {MCP_STREAM_URL}
        </code>
        <CopyButton text={MCP_STREAM_URL} />
        <Button
          variant="outline"
          size="sm"
          onClick={testConnection}
          disabled={testing}
          className="flex-none"
        >
          {testing ? (
            <>
              <CircleNotchIcon className="mr-2 h-3.5 w-3.5 animate-spin" />
              Testing…
            </>
          ) : (
            <>
              <ArrowClockwiseIcon className="mr-2 h-3.5 w-3.5" />
              Test connection
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Guided connect wizard — a beginner-friendly 3-step flow. OAuth clients only
// need the link; key-based clients get a one-click key that auto-fills the
// setup code.
// ---------------------------------------------------------------------------

// Clients that sign in with the user's Scrambl account — no API key involved.
const OAUTH_TABS: ClientTab[] = ['chatgpt', 'claude'];

const CLIENT_PASTE_HINTS: Record<ClientTab, string> = {
  chatgpt:
    'In ChatGPT, go to Settings → Connectors → Add connector and paste the link.',
  claude:
    'In Claude Desktop, go to Settings → Connectors → Add custom connector and paste the link.',
  cursor:
    'In Cursor, open Settings → MCP → Add new MCP server and paste the code.',
  windsurf:
    'In Windsurf, open Settings → MCP servers → Add server and paste the code.',
  vscode:
    "In VS Code, run 'MCP: Add Server' from the Command Palette and paste the code.",
  jetbrains:
    'In your JetBrains IDE, open Settings → Tools → AI Assistant → MCP and paste the code.',
  openclaw: 'Paste the code into your OpenClaw MCP configuration.',
  stdio:
    'For local scripts and tools that launch the Scrambl server themselves.',
};

function ConnectSteps({
  activeApiKey,
  onCreateKey,
}: {
  activeApiKey: string;
  onCreateKey: (name: string) => Promise<void>;
}) {
  const [selectedTab, setSelectedTab] = useState<ClientTab>('claude');
  const [creatingKey, setCreatingKey] = useState(false);
  const isOAuth = OAUTH_TABS.includes(selectedTab);
  const selectedLabel =
    CLIENT_TABS.find((tab) => tab.value === selectedTab)?.label ?? 'your app';
  const snippet = buildConfigSnippet(selectedTab, activeApiKey);

  const handleCreateKey = async () => {
    setCreatingKey(true);
    try {
      await onCreateKey(selectedLabel);
    } finally {
      setCreatingKey(false);
    }
  };

  return (
    <>
      <DetailSection title="Choose your AI app">
        <div className="-ml-1 flex flex-wrap gap-1.5">
          {CLIENT_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setSelectedTab(tab.value)}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                selectedTab === tab.value
                  ? 'bg-gradient-to-r from-primary/[0.12] to-primary/[0.04] text-foreground'
                  : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </DetailSection>

      <DetailSection title={isOAuth ? 'Copy this link' : 'Copy your setup code'}>
        {isOAuth ? (
          <>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md border border-border/60 bg-muted/30 px-2.5 py-2 font-mono text-xs">
                {MCP_STREAM_URL}
              </code>
              <CopyButton text={MCP_STREAM_URL} label="Copy" />
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedLabel} will ask you to sign in with your Scrambl account
              — no key or code needed.
            </p>
          </>
        ) : (
          <>
            {!activeApiKey ? (
              <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-900/20 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  {selectedLabel} needs an access key. Create one and it fills
                  into the code automatically.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="self-start sm:self-auto"
                  onClick={handleCreateKey}
                  disabled={creatingKey}
                >
                  {creatingKey ? (
                    <>
                      <CircleNotchIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    <>
                      <KeyIcon className="mr-1.5 h-3.5 w-3.5" />
                      Create key
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Your new access key is filled in below. Copy the code now — the
                key won&rsquo;t be shown again.
              </p>
            )}
            <CodeBlock code={snippet} />
          </>
        )}
      </DetailSection>

      <DetailSection title={`Paste it into ${selectedLabel}`}>
        <p className="max-w-prose text-sm text-muted-foreground">
          {CLIENT_PASTE_HINTS[selectedTab]} Then ask your AI about your Scrambl
          tasks to check it&rsquo;s working.
        </p>
      </DetailSection>
    </>
  );
}

// ---------------------------------------------------------------------------
// API Keys Section
// ---------------------------------------------------------------------------

function ApiKeys({
  apiKeys,
  onCreateKey,
  onRevokeKey,
  createdKey,
  onDismissCreatedKey,
}: {
  apiKeys: McpApiKey[];
  onCreateKey: (name: string) => Promise<void>;
  onRevokeKey: (id: string) => Promise<void>;
  createdKey: string | null;
  onDismissCreatedKey: () => void;
}) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!keyName.trim()) return;
    setCreating(true);
    try {
      await onCreateKey(keyName.trim());
      setKeyName('');
      setShowCreateDialog(false);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    setRevoking(id);
    try {
      await onRevokeKey(id);
    } finally {
      setRevoking(null);
      setConfirmRevoke(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          <ShieldWarningIcon className="mr-1 inline h-3 w-3" weight="fill" />
          Keys provide full access to your workspace. Keep them secure.
        </p>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs">
              <KeyIcon className="h-3.5 w-3.5" />
              New key
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create API key</DialogTitle>
              <DialogDescription>
                Give your key a name so you can identify it later.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-2">
                <Label htmlFor="new-key-name">Name</Label>
                <Input
                  id="new-key-name"
                  placeholder="e.g., Claude Desktop, Cursor, My Script"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={creating || !keyName.trim()}>
                {creating ? (
                  <>
                    <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  'Create key'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Newly created key banner */}
      {createdKey && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/40 dark:bg-emerald-900/20">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-emerald-900 dark:text-emerald-300">
            <KeyIcon className="h-3.5 w-3.5" weight="fill" />
            New API key created
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-background px-2 py-1.5 font-mono text-xs">
              {createdKey}
            </code>
            <CopyButton text={createdKey} />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <p className="flex items-center gap-1 text-[11px] text-emerald-800 dark:text-emerald-400">
              <WarningCircleIcon className="h-3 w-3" />
              Copy now — won&rsquo;t be shown again.
            </p>
            <Button variant="ghost" size="sm" onClick={onDismissCreatedKey} className="h-6 px-2 text-xs">
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Key list */}
      {apiKeys.length === 0 ? (
        <p className="py-1 text-sm text-muted-foreground">
          No API keys yet — create one and it appears here.
        </p>
      ) : (
        <ul className="divide-y divide-border/40">
          {apiKeys.map((key) => (
            <li
              key={key.id}
              className="group flex items-center gap-3 py-2.5"
            >
              <div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                <KeyIcon className="h-3.5 w-3.5" weight="fill" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-medium">{key.name}</span>
                  <code className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                    {key.keyPrefix}…
                  </code>
                  <Badge variant="outline" className="h-4 rounded px-1 text-[10px] font-normal">
                    Uses your module permissions
                  </Badge>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span>Created {formatDateUserTz(key.createdAt, 'MMM d, yyyy')}</span>
                  {key.lastUsedAt && (
                    <>
                      <span className="text-border">·</span>
                      <span>Last used {formatDistanceToNow(new Date(key.lastUsedAt))} ago</span>
                    </>
                  )}
                </div>
              </div>
              <Dialog
                open={confirmRevoke === key.id}
                onOpenChange={(open) => !open && setConfirmRevoke(null)}
              >
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Revoke ${key.name}`}
                    onClick={() => setConfirmRevoke(key.id)}
                    disabled={revoking === key.id}
                    className="h-7 w-7 p-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Revoke API key</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to revoke <strong>{key.name}</strong>? Any AI
                      clients using this key will immediately lose access.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setConfirmRevoke(null)}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleRevoke(key.id)}
                      disabled={revoking === key.id}
                    >
                      {revoking === key.id ? 'Revoking…' : 'Revoke key'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Usage Metrics Section
// ---------------------------------------------------------------------------

function UsageMetrics({ connection }: { connection: McpConnection | null }) {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!connection) return;
    setLoading(true);
    mcpService
      .getMetrics(24)
      .then(setMetrics)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [connection]);

  if (!connection) return null;

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (!metrics) {
    return (
      <p className="py-2 text-center text-xs text-muted-foreground">
        No usage data yet. Metrics appear once your AI clients start making requests.
      </p>
    );
  }

  const successRate =
    metrics.totalRequests > 0
      ? `${Math.round(((metrics.successfulRequests ?? 0) / metrics.totalRequests) * 100)}%`
      : '—';

  return (
    <div className="flex flex-wrap gap-x-12 gap-y-4">
      <Metric label="Total requests" value={metrics.totalRequests ?? 0} />
      <Metric
        label="Success rate"
        value={successRate}
        sub={metrics.failedRequests ? `${metrics.failedRequests} failed` : undefined}
      />
      <Metric
        label="Avg response"
        value={metrics.averageResponseTime != null ? `${metrics.averageResponseTime}ms` : '—'}
      />
      <Metric label="Active keys" value={metrics.activeApiKeys ?? 0} />
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reusable MCP Settings Content (no PageContainer wrapper)
// ---------------------------------------------------------------------------

export function McpSettingsContent() {
  const [connection, setConnection] = useState<McpConnection | null>(null);
  const [apiKeys, setApiKeys] = useState<McpApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  // Derive first active API key for config snippets
  // If we just created a key, use that; otherwise the prefix hint (full key not available after creation)
  const activeApiKey = createdKey ?? '';

  const loadData = async () => {
    try {
      setLoading(true);
      const [conn, keys] = await Promise.all([
        mcpService.getConnection().catch(() => null),
        mcpService.listApiKeys().catch(() => []),
      ]);
      setConnection(conn);
      setApiKeys(keys);

    } catch (error) {
      logger.error('Failed to load MCP data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
     
  }, []);

  const enableMcp = async () => {
    try {
      setEnabling(true);
      const newConn = await mcpService.createConnection({
        name: 'MCP Integration',
        description: 'Model Context Protocol integration for AI assistants',
      });
      setConnection(newConn);
      toast.success('MCP enabled', {
        description: 'You can now generate API keys and connect AI assistants.',
      });
    } catch (error) {
      if (error instanceof Error && 'status' in error && (error as { status: number }).status === 403) {
        toast.error('Permission denied', {
          description: 'You do not have permission to enable integrations. Please contact your admin.',
        });
      } else {
        toast.error('Failed to enable MCP');
      }
    } finally {
      setEnabling(false);
    }
  };

  const handleCreateKey = async (name: string) => {
    try {
      const data: CreateApiKeyDto = {
        name,
        connectionId: connection?.id,
      };
      const result = await mcpService.createApiKey(data);
      setCreatedKey(result.key || null);
      setApiKeys((prev) => [result, ...prev]);
      toast.success('API key created', {
        description: "Copy it now — it won't be shown again!",
      });
    } catch (error) {
      if (error instanceof Error && 'status' in error && (error as { status: number }).status === 403) {
        toast.error('Permission denied', {
          description: 'You do not have permission to manage integrations. Please contact your admin.',
        });
      } else {
        toast.error('Failed to create API key');
      }
    }
  };

  const handleRevokeKey = async (id: string) => {
    try {
      await mcpService.revokeApiKey(id);
      setApiKeys((prev) => prev.filter((k) => k.id !== id));
      toast.success('API key revoked');
    } catch (error) {
      if (error instanceof Error && 'status' in error && (error as { status: number }).status === 403) {
        toast.error('Permission denied', {
          description: 'You do not have permission to manage integrations. Please contact your admin.',
        });
      } else {
        toast.error('Failed to revoke API key');
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-44" />
        <Skeleton className="h-4 w-3/5" />
        <Skeleton className="h-24 w-full rounded-md" />
      </div>
    );
  }

  return (
    <DetailShell>
      <DetailHeader
        title="AI Assistants"
        description="Let AI apps like Claude, ChatGPT, and Cursor see and update your Scrambl work."
        status={connection ? <StatusDot label="Active" /> : undefined}
      />

      {!connection ? (
        <DetailBlock className="space-y-4">
          <div className="flex items-start gap-4">
            <RobotIcon
              className="h-9 w-9 flex-none text-primary/70"
              weight="duotone"
            />
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-medium">AI assistant access is off</p>
              <p className="max-w-prose text-sm text-muted-foreground">
                Turn it on to let AI apps like Claude, ChatGPT, and Cursor read
                and manage your Scrambl data securely.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            className="ml-[52px]"
            onClick={enableMcp}
            disabled={enabling}
          >
            {enabling ? (
              <>
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                Turning on…
              </>
            ) : (
              'Turn on AI assistant access'
            )}
          </Button>
        </DetailBlock>
      ) : (
        <>
          <ConnectSteps
            activeApiKey={activeApiKey}
            onCreateKey={handleCreateKey}
          />

          {/* Everything technical lives here, collapsed by default. */}
          <DetailBlock className="ml-11">
            <div className="h-px bg-gradient-to-r from-border to-transparent" />
            <Accordion type="single" collapsible>
              {/* Kill the AccordionItem's default card chrome (border,
                  rounding, margin) — Advanced reads as a quiet disclosure
                  under the hairline, not a boxed card. */}
              <AccordionItem
                value="advanced"
                className="mb-0 rounded-none border-0"
              >
                <AccordionTrigger className="py-3 hover:no-underline">
                  <div className="flex min-w-0 items-center gap-2">
                    <SlidersHorizontalIcon className="h-4 w-4 flex-none text-muted-foreground" />
                    <span className="text-sm font-medium">Advanced</span>
                    <span className="truncate text-xs font-normal text-muted-foreground">
                      Server details, API keys & usage
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-7 pt-2">
                  <div className="space-y-3">
                    <h4 className="flex items-center gap-1.5 text-[13px] font-semibold">
                      <PulseIcon className="h-3.5 w-3.5 text-muted-foreground" />
                      Server
                    </h4>
                    <ConnectionStatus
                      connection={connection}
                      onEnable={enableMcp}
                      enabling={enabling}
                    />
                  </div>
                  <div className="space-y-3">
                    <h4 className="flex items-center gap-1.5 text-[13px] font-semibold">
                      <KeyIcon className="h-3.5 w-3.5 text-muted-foreground" />
                      API keys
                      {apiKeys.length > 0 && (
                        <span className="text-xs font-normal tabular-nums text-muted-foreground">
                          {apiKeys.length}
                        </span>
                      )}
                    </h4>
                    <ApiKeys
                      apiKeys={apiKeys}
                      onCreateKey={handleCreateKey}
                      onRevokeKey={handleRevokeKey}
                      createdKey={createdKey}
                      onDismissCreatedKey={() => setCreatedKey(null)}
                    />
                  </div>
                  <div className="space-y-3">
                    <h4 className="flex items-center gap-1.5 text-[13px] font-semibold">
                      <PulseIcon className="h-3.5 w-3.5 text-muted-foreground" />
                      Usage (last 24h)
                    </h4>
                    <UsageMetrics connection={connection} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </DetailBlock>
        </>
      )}
    </DetailShell>
  );
}

// ---------------------------------------------------------------------------
// Standalone Page (with PageContainer wrapper)
// ---------------------------------------------------------------------------

export default function McpSettingsPage() {
  return (
    <PageContainer
      header={{
        title: 'AI Assistant Integration',
        description: 'Connect AI tools to your Scrambl workspace via MCP',
      }}
    >
      <McpSettingsContent />
    </PageContainer>
  );
}
